import i18n from "@/lib/i18n";

/** Fallback IANA timezone used when the tenant timezone is unavailable. */
export const DEFAULT_TENANT_TZ = "Europe/Minsk";

/** Strips the seconds component from a "HH:MM[:SS]" time string. */
function stripSeconds(time: string): string {
  return time.slice(0, 5);
}

/**
 * Formats a delivery time window as "HH:MM–HH:MM".
 *
 * The backend stores window bounds as plain time strings (e.g. "18:30:00").
 * Seconds are dropped for display. Returns "—" when either bound is missing
 * or when start equals end (a zero-length window means "no window").
 *
 * @param start Window start time string ("HH:MM:SS"), or null.
 * @param end   Window end time string ("HH:MM:SS"), or null.
 * @returns "HH:MM–HH:MM", or "—" when there is no meaningful window.
 */
export function formatTimeWindow(
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  if (!start || !end || start === end) return "—";
  return `${stripSeconds(start)}–${stripSeconds(end)}`;
}

/**
 * Formats a stored UTC instant in the tenant's timezone.
 *
 * The backend stores route times as correct UTC instants. To display them in
 * the tenant's intended local time regardless of the dispatcher's browser
 * timezone, we render with an explicit `timeZone` option. Only the zone is
 * forced — the locale follows the active i18n language so RU/EN month and time
 * formatting matches the rest of the app.
 *
 * @param iso    ISO 8601 datetime string (UTC instant), or null.
 * @param tz     IANA timezone name (e.g. "Europe/Minsk").
 * @param opts   Intl.DateTimeFormat options. Defaults to date + time (HH:MM).
 * @param locale BCP-47 locale; defaults to the active i18n language.
 * @returns Formatted string, or "—" when `iso` is null/empty.
 */
export function formatInTenantTz(
  iso: string | null | undefined,
  tz: string,
  opts?: Intl.DateTimeFormatOptions,
  locale: string = i18n.language,
): string {
  if (!iso) return "—";
  const options: Intl.DateTimeFormatOptions = opts ?? {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  };
  return new Intl.DateTimeFormat(locale, {
    // Keep the dispatcher's consistent 24-hour clock across locales (the app
    // standard, mirroring `formatDateTime` in lib/format). Only month/day NAMES
    // localize; the time stays 24h so English does not regress to AM/PM. An
    // explicit `hour12` in `options` still wins.
    hour12: false,
    ...options,
    timeZone: tz || DEFAULT_TENANT_TZ,
  }).format(new Date(iso));
}

/**
 * The ISO day immediately BEFORE a given ISO `YYYY-MM-DD` date.
 *
 * Derived from the date STRING, never from the clock: the dashboard's
 * yesterday-delta must be the day before whatever «today» the panel is showing.
 * Computing it from `Date.now() - 86400000` (the removed `getYesterdayISO`)
 * re-reads the clock in UTC, so after BUG-529 moved "today" into the tenant's
 * timezone the two would disagree for the same three-hour window — the
 * comparison window would silently shift by a day in the opposite direction.
 *
 * Parsed as UTC (`T00:00:00Z`) and stepped by one UTC day, so it is immune to
 * the browser's own offset and to DST: the input carries no time, and the output
 * is pure calendar arithmetic.
 *
 * @param iso ISO `YYYY-MM-DD` date.
 * @returns ISO `YYYY-MM-DD` for the preceding day.
 */
export function yesterdayOf(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

/**
 * "Today" as an ISO `YYYY-MM-DD` date in the TENANT's timezone.
 *
 * The counterpart of the backend's `apps.core.timezones.tenant_today`, and the
 * reason the old `getTodayISO` was not good enough for anything tenant-facing
 * (removed in TASK-5043): that one sliced a UTC instant, so between 21:00 and
 * 24:00 in Minsk it returned *yesterday*. Comparing a dispatcher's scheduled
 * dates against it would call a date "past" for three hours every evening — the
 * BUG-491 boundary-day family.
 *
 * `en-CA` is not a locale choice: it is the one widely-supported locale whose
 * numeric format is exactly ISO `YYYY-MM-DD`, so the parts need no reassembly.
 *
 * @param tz IANA timezone name (e.g. "Europe/Minsk").
 * @returns ISO `YYYY-MM-DD` for the current day in `tz`.
 */
export function tenantTodayISO(tz: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: tz || DEFAULT_TENANT_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

/**
 * The UTC offset of `tz` at a given instant, in minutes east of UTC.
 *
 * Derived by formatting the instant AS the tenant would see it and reading the
 * difference back as a UTC timestamp — the same `Intl` route
 * `activityStrip.ts:36` takes, and for the same reason: there is no way to ask
 * a `Date` for another zone's offset, and `getTimezoneOffset()` answers for the
 * DISPATCHER's browser. Because the offset is read AT the instant, DST is
 * handled by construction rather than by a table.
 */
function tzOffsetMinutes(instant: Date, tz: string): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: tz || DEFAULT_TENANT_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(instant);
  const get = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value ?? "0");
  // `hour12: false` renders midnight as "24" in some ICU versions — normalize
  // (the same guard `activityStrip.ts` carries).
  const asUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour") % 24,
    get("minute"),
    get("second"),
  );
  // `instant` carries milliseconds the formatted parts do not, so the raw
  // difference is off by up to 999ms. Offsets are whole minutes, so rounding to
  // the nearest minute recovers the exact value.
  return Math.round((asUtc - instant.getTime()) / 60_000);
}

/**
 * The UTC instant of midnight starting the ISO day `isoDate` in `tz`.
 *
 * Two passes, not one: the offset to apply is the offset IN EFFECT at the
 * resulting instant, which cannot be known before that instant is computed. The
 * first pass guesses with the offset at the naive timestamp; the second
 * re-reads the offset at the guess and corrects. On a DST-transition night
 * (where the two differ by an hour) this converges; on every other day the
 * second pass is a no-op.
 */
function tenantMidnightUtc(isoDate: string, tz: string): Date {
  const naive = Date.parse(`${isoDate}T00:00:00Z`);
  const firstGuess = new Date(
    naive - tzOffsetMinutes(new Date(naive), tz) * 60_000,
  );
  return new Date(naive - tzOffsetMinutes(firstGuess, tz) * 60_000);
}

/** An inclusive instant window: what the tracking endpoints take as `from`/`to`. */
export interface TenantDayWindow {
  /** ISO instant of the tenant's midnight opening the day. */
  from: string;
  /** ISO instant one millisecond before the tenant's next midnight. */
  to: string;
}

/**
 * The `{from, to}` UTC instants bounding ONE tenant-local day.
 *
 * TASK-5041: this is the single definition of "a tenant's day" for the client.
 * `LiveTrackingPage` and `useVehicleDistanceToday` each used to carry their own
 * copy of
 *
 *   const todayFrom = new Date(); todayFrom.setUTCHours(0, 0, 0, 0);
 *   const todayTo   = new Date(); todayTo.setUTCHours(23, 59, 59, 999);
 *
 * which bounds the UTC day. For Europe/Minsk (UTC+3) that window omits
 * 00:00–03:00 of the local day and includes 00:00–03:00 of the NEXT one — the
 * dispatcher's «пробег сегодня» and day track described the wrong interval
 * (~95 GPS points per day, measured on dev). Two copies of a boundary rule is
 * how they drift, so there is exactly one here.
 *
 * The end is `next midnight − 1ms` rather than `23:59:59.999` because a
 * tenant-local day is not always 24h long: on a DST-transition day it is 23 or
 * 25, and deriving the end from the NEXT day's midnight is correct in all
 * three cases. It stays INCLUSIVE because that is the contract the tracking
 * endpoints' `from`/`to` params already have.
 *
 * @param tz      IANA timezone name (e.g. "Europe/Minsk").
 * @param isoDate Tenant-local day to bound; defaults to the tenant's «today».
 */
export function tenantDayWindow(
  tz: string,
  isoDate: string = tenantTodayISO(tz),
): TenantDayWindow {
  const from = tenantMidnightUtc(isoDate, tz);
  const nextDay = new Date(`${isoDate}T00:00:00Z`);
  nextDay.setUTCDate(nextDay.getUTCDate() + 1);
  const nextMidnight = tenantMidnightUtc(
    nextDay.toISOString().slice(0, 10),
    tz,
  );
  return {
    from: from.toISOString(),
    to: new Date(nextMidnight.getTime() - 1).toISOString(),
  };
}

/**
 * `days` days before the tenant's «today», as ISO `YYYY-MM-DD`.
 *
 * Stepped in UTC from the tenant's own date STRING (which carries no time), so
 * the arithmetic is pure calendar maths — immune to the browser's offset and to
 * DST, and always exactly `days` wide. The same shape as
 * `routesDateWindow.defaultDateFrom`, generalized over the span.
 *
 * What it replaces (TASK-5041): `analyticsFilters.daysAgoStr` and its duplicate
 * in `AnalyticsFilterBar` each did `d.setDate(d.getDate() - days)` followed by
 * `.toISOString().slice(0, 10)` — browser-LOCAL day arithmetic read back as a
 * UTC date, two different zones inside one four-line function.
 */
export function tenantDaysAgoISO(tz: string, days: number): string {
  const d = new Date(`${tenantTodayISO(tz)}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

/**
 * The first day of the tenant's current month, as ISO `YYYY-MM-DD`.
 *
 * Taken from the tenant's own date string rather than the clock: on the 1st of
 * the month between 00:00 and 03:00 Minsk, `new Date()` is still in the
 * previous UTC month, so the old `firstOfMonthStr` returned the first of the
 * WRONG month.
 */
export function tenantFirstOfMonthISO(tz: string): string {
  return `${tenantTodayISO(tz).slice(0, 7)}-01`;
}
